import { useEffect, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { Bell, BellOff, Megaphone, X } from "@fg/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useSendThreadReply, useThreadReplies } from "@/api/hooks";
import { normalizeUserId, type ChannelMember, type ChatMessage } from "@/api/connectApi";
import { flashMessage } from "@/lib/jump";
import { ChatComposer, type ChatShareTarget, type MentionCandidate } from "./chat/ChatComposer";
import { ChatAvatar, timeLabel } from "./chat/chatUi";
import { MessageBody } from "./files/MessageBody";
import { useDriveAttachments } from "./files/useDriveAttachments";

/**
 * A channel post's full reply thread, in the side panel (Teams "Replies").
 * Replies use the chat composer — Markdown, emoji, @mentions of the channel's
 * members and Drive files shared with them. New replies arrive over the hub
 * (channel.message.sent with a parent id invalidates this thread).
 */
export function ThreadPanel({
    channelId,
    channelName,
    parent,
    members,
    highlightId,
    onClose
}: {
    channelId: string;
    channelName: string;
    parent: ChatMessage;
    members: ChannelMember[];
    /** A reply to scroll to and flash once loaded (from search or a pin). */
    highlightId?: string | null;
    onClose: () => void;
}) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const token = useAccessToken();
    const qc = useQueryClient();
    // Following a thread puts its replies in Activity; you follow ones you started or replied in until you say otherwise.
    const follow = useQuery({
        queryKey: ["thread-follow", parent.id],
        queryFn: () => connectParityApi.getThreadFollow(parent.id, token),
        enabled: Boolean(token)
    });
    const toggleFollow = async () => {
        const next = !(follow.data?.following ?? false);
        qc.setQueryData(["thread-follow", parent.id], { following: next });
        try { await connectParityApi.setThreadFollow(parent.id, next, token); }
        catch { void qc.invalidateQueries({ queryKey: ["thread-follow", parent.id] }); }
    };
    const replies = useThreadReplies(channelId, parent.id);
    const send = useSendThreadReply(channelId, parent.id);
    const attachments = useDriveAttachments("view");
    const clearAttachments = attachments.clear;
    useEffect(() => { clearAttachments(); }, [parent.id, clearAttachments]);

    const list = useMemo(() => replies.data ?? [], [replies.data]);
    const scrollerRef = useRef<HTMLDivElement | null>(null);

    // Follow new replies — unless we're jumping to a particular one.
    useEffect(() => {
        if (highlightId) return;
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [list.length, highlightId]);
    useEffect(() => {
        if (!highlightId || !list.some((r) => r.id === highlightId)) return;
        window.requestAnimationFrame(() => flashMessage(`reply-${highlightId}`));
    }, [highlightId, list]);

    const mentionCandidates = useMemo<MentionCandidate[]>(() =>
        members
            .filter((m) => normalizeUserId(m.userId) !== me)
            .map((m) => ({ userId: m.userId, name: m.displayName || m.userEmail, email: m.userEmail })),
    [members, me]);

    const resolveShare = async (): Promise<ChatShareTarget> => ({
        emails: members.map((m) => m.userEmail).filter((e) => e && e.toLowerCase() !== myEmail),
        ownerEmail: myEmail,
        source: "connect-channel",
        sourceRef: channelId
    });

    const parentName = parent.authorDisplayName ?? parent.authorEmail;

    return (
        <aside
            className="fixed inset-0 z-40 flex w-full flex-col border-l bg-background md:static md:z-auto md:w-96 md:shrink-0"
            aria-label="Thread"
            onKeyDown={(e) => {
                if (e.key === "Escape" && !e.defaultPrevented) onClose();
            }}
        >
            <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4">
                <div className="min-w-0">
                    <h2 className="text-sm font-semibold">Thread</h2>
                    <p className="truncate text-[11px] text-muted-foreground">#{channelName}</p>
                </div>
                <button
                    type="button"
                    onClick={() => void toggleFollow()}
                    disabled={follow.isPending}
                    aria-pressed={follow.data?.following ?? false}
                    title={follow.data?.following ? "Following: replies show in Activity. Click to unfollow." : "Follow this thread to get its replies in Activity"}
                    className="ml-auto flex h-7 items-center gap-1 rounded-md px-2 text-xs hover:bg-accent disabled:opacity-50"
                >
                    {follow.data?.following ? <><BellOff className="h-3.5 w-3.5" /> Unfollow</> : <><Bell className="h-3.5 w-3.5" /> Follow</>}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close thread"
                    className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                >
                    <X className="h-4 w-4" />
                </button>
            </header>
            <div ref={scrollerRef} className="min-h-0 flex-1 overflow-auto px-4 py-3">
                <article className="overflow-hidden rounded-lg border bg-card">
                    {parent.isAnnouncement && parent.subject ? (
                        <div className="bg-gradient-to-r from-primary to-violet-600 px-3 py-3 text-white">
                            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-90">
                                <Megaphone className="h-3 w-3" aria-hidden /> Announcement
                            </div>
                            <p className="mt-0.5 break-words text-base font-bold leading-tight">{parent.subject}</p>
                        </div>
                    ) : null}
                    <div className="p-3">
                        <div className="flex items-center gap-2 text-xs">
                            <ChatAvatar name={parentName} size="sm" />
                            <span className="font-semibold">{parentName}</span>
                            <time dateTime={parent.sentAt} className="text-muted-foreground">
                                {new Date(parent.sentAt).toLocaleString()}
                            </time>
                        </div>
                        {parent.subject && !parent.isAnnouncement ? (
                            <p className="mt-1.5 break-words text-sm font-semibold">{parent.subject}</p>
                        ) : null}
                        <MessageBody html={parent.bodyHtml} className="prose prose-sm mt-1 max-w-none break-words dark:prose-invert" />
                    </div>
                </article>
                <div className="my-3 flex items-center gap-2 text-xs text-muted-foreground" role="separator">
                    <span className="flex-1 border-t border-dashed" />
                    {list.length} {list.length === 1 ? "reply" : "replies"}
                    <span className="flex-1 border-t border-dashed" />
                </div>
                {replies.isPending ? (
                    <p className="text-sm text-muted-foreground">Loading replies…</p>
                ) : list.length > 0 ? (
                    <ol className="space-y-1" aria-label="Replies">
                        {list.map((r) => {
                            const name = r.authorDisplayName ?? r.authorEmail;
                            const at = new Date(r.sentAt);
                            return (
                                <li key={r.id} id={`reply-${r.id}`} className="flex gap-2 rounded-md px-1.5 py-1.5 hover:bg-muted/40">
                                    <ChatAvatar name={name} size="sm" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline gap-2 text-xs">
                                            <span className="font-semibold">{name}</span>
                                            <time dateTime={r.sentAt} title={at.toLocaleString()} className="text-muted-foreground">
                                                {timeLabel(at)}
                                            </time>
                                            {r.isEdited ? <span className="text-[10px] text-muted-foreground">(edited)</span> : null}
                                        </div>
                                        <MessageBody html={r.bodyHtml} className="prose prose-sm mt-0.5 max-w-none break-words dark:prose-invert" />
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                ) : (
                    <p className="text-sm text-muted-foreground">No replies yet — start the conversation below.</p>
                )}
            </div>
            <ChatComposer
                placeholder="Reply"
                onSend={async (html) => { await send.mutateAsync(html); }}
                attachments={attachments}
                resolveShare={resolveShare}
                mentionCandidates={mentionCandidates}
                requireRecipients={false}
            />
        </aside>
    );
}
